import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useDeviceList } from '../../../../hooks/useDeviceList';
import { useDeviceDisplayName } from '../../../../hooks/useDeviceDisplayName';
import { updateDeviceDisplayName } from '../dms-setting/api';
import Log from '../../../../Logger';
import { FeatureContext } from '../../../FeatureContext';

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
};

// 編集中のデバイス表示情報を表す型
type EditDeviceDisplayInfo = {
    serialId: string;
    displayName: string;
    editableDisplayName: string;
    validationError?: string;
};

/**
 * @module DeviceNameDialog
 * @description
 * DeviceNameDialogコンポーネントは、テナントに属するデバイスの表示名を一覧表示・編集・保存するためのダイアログUIを提供します。
 * 管理者権限に合わせたUIの制御やバリデーション機能、新規デバイスの自動登録も行います。
 *
 * @param {Props} props - コンポーネントのプロパティ
 * @param {boolean} props.open - ダイアログの表示状態
 * @param {function} props.onClose - ダイアログを閉じる際のコールバック関数
 * @param {function} [props.onSuccess] - 保存成功時のコールバック関数（成功メッセージを受け取る）
 * @param {function} [props.onError] - エラー発生時のコールバック関数（エラーメッセージを受け取る）
 *
 * @returns {React.ReactElement} レンダリングされるReact要素
 */
export default function DeviceNameDialog({ open, onClose, onSuccess, onError }: Props) {
    const logger = Log.getLogger();
    const features = React.useContext(FeatureContext);

    const { deviceList, isLoading, error } = useDeviceList();
    const { mapping, refetch: refetchDeviceDisplayName } = useDeviceDisplayName();

    const [deviceDisplayInfos, setDeviceDisplayInfos] = useState<EditDeviceDisplayInfo[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isAutoRegistering, setIsAutoRegistering] = useState(false);

    // ダイアログが開いた時に行う処理
    useEffect(() => {
        //デバイス情報を初期化
        if (open && deviceList && deviceList.length > 0) {
            const infos = deviceList.map(device => ({
                deviceId: device.deviceId,
                serialId: device.serialId,
                displayName: device.name || '',
                editableDisplayName: device.name || ''
            }));
            setDeviceDisplayInfos(infos);
            // 新規デバイスの自動登録を実行(管理者のみ)
            if (features.canUpdateDeviceDisplayName()) {
                autoRegisterNewSerialIds();
            }
        }
    }, [open, deviceList]);

    /**
     * 設定ボタンを押したときに呼び出される関数
     * 
     * @description
     * deviceListの中で、表示名マッピングに存在しないシリアルIDを検出し、
     * それらをシリアルIDのまま表示名として登録する。
     * deviceListと差異がない場合は処理を終了する。
     * `setAutoRegistering` が `true`の間、処理中を示すローディング表示を行う。
     */
    const autoRegisterNewSerialIds = async () => {
        const newSerialIds: string[] = [];
        // マッピングにないシリアルIDを検出
        for (const device of deviceList) {
            if (mapping && !mapping.has(device.serialId)) {
                newSerialIds.push(device.serialId);
            }
        }
        //新しいデバイスが無ければ処理を終了
        if (newSerialIds.length === 0) return;

        setIsAutoRegistering(true);
        try {
            logger.ui.info('DeviceNameDialog', 'start auto-registering new serialId');

            // 新規シリアルIDをシリアルIDのまま表示名として登録
            const mappings = newSerialIds.map(serialId => ({
                serialId,
                displayName: serialId // 初期値はシリアルIDと同じ
            }));
            const resp = await updateDeviceDisplayName(mappings);
            if (!resp) {
                throw new Error('updateDeviceDisplayName response is null');
            }

            logger.ui.info('DeviceNameDialog', 'success to auto-register new serialId');

            // キャッシュを更新
            await refetchDeviceDisplayName();
        } catch (error) {
            logger.ui.error('DeviceNameDialog', 'fail to auto-register new serialId', error as string);
            // 登録失敗時はエラーメッセージを表示し、設定画面を閉じる
            onError?.('新しいデバイスの登録に失敗しました。再度試しても登録できない場合は、お手数ですがサポートまでお問い合わせください。');
            onClose();
        } finally {
            setIsAutoRegistering(false);
        }
    };

    // 表示名変更中の処理
    const handleDisplayNameChange = (serialId: string, newValue: string) => {
        // バリデーションチェック
        let validationError = '';
        const MAX_DEVICE_NAME_LENGTH = 50;
        if (newValue.length > MAX_DEVICE_NAME_LENGTH) {
            validationError = `! ${MAX_DEVICE_NAME_LENGTH}文字以上は設定できません`;
        }
        const symbolPattern = /[%!@#$^&*()+=\[\]{}|\\:";'<>?,./`~]/;
        if (symbolPattern.test(newValue)) {
            validationError = '! 記号は設定できません';
        }
        setDeviceDisplayInfos(prev =>
            prev.map(device =>
                device.serialId === serialId
                    ? { ...device, editableDisplayName: newValue, validationError }
                    : device
            )
        );
    };

    // 保存ボタンを押したときの保存処理
    const handleSave = async () => {
        setIsSaving(true);
        try {
            logger.ui.info('DeviceNameDialog', 'start saving displayName');

            // 変更があったデバイスのみ保存
            const changedDevices = deviceDisplayInfos.filter(device => {
                const original = device.displayName?.trim() || '';
                const editable = device.editableDisplayName?.trim() || '';
                return original !== editable;
            });

            if (changedDevices.length === 0) {
                setIsSaving(false);
                onClose();
                return;
            }

            const mappings = changedDevices.map(device => ({
                serialId: device.serialId,
                displayName: device.editableDisplayName?.trim() || device.serialId
            }));
            //表示名を登録
            await updateDeviceDisplayName(mappings);
            logger.ui.info('DeviceNameDialog', 'success to update device display name');

            // キャッシュを更新
            await refetchDeviceDisplayName();

            setIsSaving(false);
            onClose();
            onSuccess?.('表示名の保存に成功しました');

        } catch (error) {
            logger.ui.error('DeviceNameDialog', 'fail to update device display name', error as string);
            setIsSaving(false);
            onClose();
            onError?.('表示名の保存に失敗しました。再度試しても取得できない場合は、お手数ですがサポートまでお問い合わせください。');
        }
    };

    // キャンセルボタンを押したときの処理
    const handleCancel = () => {
        setDeviceDisplayInfos(prev =>
            prev.map(device => ({
                ...device,
                editableDisplayName: device.displayName || '',
                validationError: undefined
            }))
        );
        onClose();
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle
                sx={{ fontSize: '1.5rem', fontWeight: 'bold', mt: 2 }}>
                デバイス名設定
            </DialogTitle>
            <DialogContent>
                {isLoading || isAutoRegistering ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            {isAutoRegistering ? '新しいデバイス情報を登録中...' : 'デバイス情報を読み込み中...'}
                        </Typography>
                    </Box>
                ) : error ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography variant="body1">
                            デバイスの取得に失敗しました。再度試しても取得できない場合はお手数ですがサポートまでお問い合わせください。
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ width: '100%' }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                }}>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                    }}>
                                        シリアルID
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                    }}>
                                        表示名
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deviceDisplayInfos.map((device) => {
                                    return (
                                        <TableRow key={device.serialId} hover>
                                            <TableCell sx={{
                                                fontSize: '1rem',
                                                py: 1
                                            }}>
                                                {device.serialId}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontSize: '1rem',
                                                py: 1
                                            }}>
                                                <Box sx={{ height: '100%', alignItems: 'center' }}>
                                                    {/* 管理者のみ編集できるようにする */}
                                                    <TextField
                                                        {...(!features.canUpdateDeviceDisplayName() && { inputProps: { readOnly: true } })}
                                                        value={device.editableDisplayName}
                                                        fullWidth
                                                        onChange={(e) => handleDisplayNameChange(device.serialId, e.target.value)}
                                                        placeholder={device.serialId}
                                                        variant="outlined"
                                                        disabled={isSaving || isAutoRegistering}
                                                        error={!!device.validationError}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: device.validationError ? '#ffebee' : 'transparent',
                                                                fontSize: '1rem',
                                                                height: '100%',
                                                                minHeight: '32px',
                                                                '&.Mui-error': {
                                                                    '& fieldset': {
                                                                        borderColor: '#f44336',
                                                                    },
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                padding: '8px 12px',
                                                            }
                                                        }}
                                                    />
                                                    {device.validationError && (
                                                        <Typography
                                                            variant="caption"
                                                            color="error"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.5,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {device.validationError}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: 2, gap: 1 }}>
                <Button
                    onClick={handleCancel}
                    disabled={isSaving || isAutoRegistering}
                    sx={{
                        color: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                        }
                    }}
                >
                    キャンセル
                </Button>
                {/* デバイス情報取得失敗時、もしくは管理者ではない場合は保存ボタンを非表示 */}
                {!features.canUpdateDeviceDisplayName() || !error && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={isSaving || isAutoRegistering || deviceDisplayInfos.some(device => device.validationError)}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                            '&:disabled': {
                                backgroundColor: 'grey.400',
                            }
                        }}
                    >
                        {isSaving ? '保存中...' : isAutoRegistering ? '登録中...' : '保存'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}